import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import SignaturePad from "@/components/SignaturePad";
import DamageDiagramCanvas from "@/components/inspection/DamageDiagramCanvas";
import { ClipboardCheck, Save, Loader2, Lock, AlertTriangle, Fuel, Gauge, Car as CarIcon, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const FUEL_LEVELS = [
  { value: "empty", label: "Empty" },
  { value: "quarter", label: "1/4" },
  { value: "half", label: "1/2" },
  { value: "three_quarters", label: "3/4" },
  { value: "full", label: "Full" },
];

/**
 * Pickup/Return checklist UI for the rental agreement page. Two stacked cards (one per
 * phase). Each card lets the participant tick off items, jot a note, set fuel level,
 * record the odometer, and sign. Once BOTH parties sign a phase, that card locks (read
 * -only with a "Locked" pill) and the phase shows `completed_at`. The return phase
 * additionally surfaces the mileage-overage chip + a deep link to the deposit-withhold
 * action in `VendorBookingsList` so the vendor can settle in one click.
 *
 * Renders only when a checklist exists OR the booking is in a state where one should
 * (confirmed). For cancelled bookings we hide it entirely — no point inspecting a car
 * that won't change hands.
 */
export default function PickupReturnChecklist({ booking }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    if (!booking?.id) return;
    api.get(`/bookings/${booking.id}/checklist`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 403 ? null : formatApiError(e)));
  };
  useEffect(() => { load(); }, [booking?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (booking?.status === "cancelled") return null;
  if (error) return <div className="text-sm text-rose-700 mt-6">Checklist: {error}</div>;
  if (!data) return null;

  const isCustomer = user?.id === booking.customer_id;
  const isVendor = user?.id === booking.vendor_id;
  const myParty = isCustomer ? "customer" : isVendor ? "vendor" : (user?.role === "admin" ? "vendor" : null);

  return (
    <section className="mt-12 print:break-inside-avoid" data-testid="pickup-return-checklist">
      <h2 className="font-display text-2xl font-medium mb-4 flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-neutral-500" />
        Vehicle handover checklist
      </h2>

      {/* Iter 67dr — Body Type / Car Body Style banner. Anchors every
          accident / scratch / dent record on either phase to the right
          body shape; vendor / admin can override per-booking. */}
      <BodyTypeBanner
        data={data}
        bookingId={booking.id}
        canEdit={!!data.can_edit_body_type}
        onChanged={load}
      />

      {/* Iter 67ff — Two phase cards stacked vertically (was lg:grid-cols-2)
          so the damage diagrams get full available width on every screen.
          The previous side-by-side layout shrank each diagram to ~half the
          column, making damage markers hard to place accurately. */}
      <div className="grid grid-cols-1 gap-5">
        <PhaseCard phase="pickup" title="Pickup" data={data} myParty={myParty} bookingId={booking.id} onChanged={load} />
        <PhaseCard phase="return" title="Return" data={data} myParty={myParty} bookingId={booking.id} onChanged={load} />
      </div>

      {/* Mileage overage banner — visible when return is complete + policy limited + overage > 0
          AND the deposit hasn't been settled yet (after withhold/release, no point nudging). */}
      {data.return?.completed_at
        && data.mileage_overage?.applicable
        && data.mileage_overage.overage_km > 0
        && (!booking.deposit_status || booking.deposit_status === "held") && (
        <OverageBanner overage={data.mileage_overage} bookingId={booking.id} userIsVendor={isVendor || user?.role === "admin"} onWithheld={load} />
      )}
    </section>
  );
}

function PhaseCard({ phase, title, data, myParty, bookingId, onChanged }) {
  const phaseState = data[phase] || {};
  const template = data.template || [];
  const completed = !!phaseState.completed_at;
  const myPartySigned = !!phaseState[`${myParty}_signature`];

  // Build a stable items map from the template, overlaying any persisted ticks/notes.
  const persistedById = new Map((phaseState.items || []).map((i) => [i.id, i]));
  const initialItems = template.map((t) => {
    const saved = persistedById.get(t.id) || {};
    return { id: t.id, label: t.label, checked: !!saved.checked, note: saved.note || "" };
  });

  const [items, setItems] = useState(initialItems);
  const [fuelLevel, setFuelLevel] = useState(phaseState.fuel_level || "");
  const [mileage, setMileage] = useState(phaseState.mileage_reading ?? "");
  const [notes, setNotes] = useState(phaseState.notes || "");
  const [drawing, setDrawing] = useState("");
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  // Iter 67ec — damage diagram markers for this phase. Edits buffer in
  // local state; persisted to the booking on Save.
  const [damageMarkers, setDamageMarkers] = useState(phaseState.damage_markers || []);
  // Iter 67ed — explicit acknowledgement gate. The signature is only
  // enabled when the user confirms that the diagram captures every
  // damage they observed. This text appears verbatim on the rental
  // agreement PDF too so it doubles as the legal anchor.
  const [damageAck, setDamageAck] = useState(false);

  // Reset state when parent reloads (e.g. after sign).
  useEffect(() => {
    setItems(initialItems);
    setFuelLevel(phaseState.fuel_level || "");
    setMileage(phaseState.mileage_reading ?? "");
    setNotes(phaseState.notes || "");
    setDrawing("");
    setDamageMarkers(phaseState.damage_markers || []);
    setDamageAck(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseState.updated_at, phaseState.completed_at, phaseState.customer_signed_at, phaseState.vendor_signed_at]);

  const toggleItem = (id, key, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/bookings/${bookingId}/checklist`, {
        phase,
        items,
        fuel_level: fuelLevel || null,
        mileage_reading: mileage === "" ? null : Number(mileage),
        notes,
        damage_markers: damageMarkers,
      });
      toast.success(`${title} checklist saved`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const sign = async () => {
    if (!drawing) { toast.error("Draw your signature first"); return; }
    setSigning(true);
    try {
      // Save current state first so the signature doesn't lock unsaved edits.
      await api.patch(`/bookings/${bookingId}/checklist`, {
        phase,
        items,
        fuel_level: fuelLevel || null,
        mileage_reading: mileage === "" ? null : Number(mileage),
        notes,
        damage_markers: damageMarkers,
      });
      await api.post(`/bookings/${bookingId}/checklist/sign`, { phase, party: myParty, image: drawing });
      toast.success(`${title} signed`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSigning(false); }
  };

  return (
    <div
      className={`rounded-2xl border border-border bg-white p-5 ${completed ? "bg-emerald-50/30 border-emerald-200" : ""}`}
      data-testid={`checklist-phase-${phase}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg font-medium flex items-center gap-2">
          {title}
          {completed && <Badge className="bg-emerald-600">Complete</Badge>}
          {!completed && phaseState.updated_at && <Badge variant="secondary">In progress</Badge>}
        </div>
        {!myParty && <span className="text-xs text-neutral-400 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Read-only</span>}
      </div>

      <div className="space-y-2 mb-4">
        {items.map((it) => (
          <div key={it.id} className="rounded-lg border border-border p-2.5 bg-neutral-50/50">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={it.checked}
                disabled={completed || !myParty}
                onChange={(e) => toggleItem(it.id, "checked", e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-neutral-300"
                data-testid={`check-${phase}-${it.id}`}
              />
              <span className={`text-sm ${it.checked ? "text-neutral-900" : "text-neutral-700"} flex-1`}>{it.label}</span>
            </label>
            {it.checked && (
              <Input
                placeholder="Notes (optional)"
                value={it.note}
                disabled={completed || !myParty}
                onChange={(e) => toggleItem(it.id, "note", e.target.value)}
                className="mt-2 rounded-lg text-xs h-8"
                data-testid={`note-${phase}-${it.id}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-xs flex items-center gap-1"><Fuel className="h-3 w-3" />Fuel level</Label>
          <Select value={fuelLevel || "__none__"} onValueChange={(v) => setFuelLevel(v === "__none__" ? "" : v)} disabled={completed || !myParty}>
            <SelectTrigger className="mt-1 rounded-xl h-9" data-testid={`fuel-${phase}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Not set —</SelectItem>
              {FUEL_LEVELS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Gauge className="h-3 w-3" />Odometer (km)</Label>
          <Input
            type="number"
            min="0"
            value={mileage}
            disabled={completed || !myParty}
            onChange={(e) => setMileage(e.target.value)}
            className="mt-1 rounded-xl h-9"
            placeholder="e.g. 50000"
            data-testid={`mileage-${phase}`}
          />
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs">General notes</Label>
        <Textarea
          rows={2}
          value={notes}
          disabled={completed || !myParty}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 rounded-xl text-sm"
          placeholder="Anything else worth recording?"
          data-testid={`notes-${phase}`}
        />
      </div>

      {/* Iter 67ec — damage inspection diagram. Renders a vehicle outline
          based on the booking's body type and lets the inspector tap to
          plot scratches, dents, broken parts, paint damage and missing
          items per view. Read-only when the phase is locked.

          Iter 67ed — on the return phase, we feed the pickup phase's
          markers in as `compareMarkers` so vendors see them as dashed
          ghosts under the active set. Newly-added damage stands out. */}
      <div className="mb-3">
        <Label className="text-xs flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 text-amber-600" />
          Vehicle damage diagram
          {phase === "return" && (data.pickup?.damage_markers?.length || 0) > 0 && (
            <span className="ml-1 text-[10px] uppercase tracking-wider font-bold text-neutral-500">
              · comparing against pickup
            </span>
          )}
        </Label>
        <div className="mt-1">
          <DamageDiagramCanvas
            bodyType={phaseState.damage_body_type || data.body_type}
            markers={damageMarkers}
            onChange={setDamageMarkers}
            canEdit={!completed && !!myParty}
            testId={`damage-diagram-${phase}`}
            compareMarkers={phase === "return" ? (data.pickup?.damage_markers || []) : null}
            compareLabel={phase === "return" ? "Pickup" : null}
          />
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-border">
        <SignatureSlotCompact party="vendor" image={phaseState.vendor_signature} signedAt={phaseState.vendor_signed_at} />
        <SignatureSlotCompact party="customer" image={phaseState.customer_signature} signedAt={phaseState.customer_signed_at} />
      </div>

      {/* Edit / Sign actions — hidden when complete or when viewer has no party */}
      {!completed && myParty && (
        <div className="mt-4 space-y-3 print:hidden">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={save} disabled={saving} className="rounded-full" data-testid={`save-${phase}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
            {!myPartySigned && (
              <span className="text-xs text-neutral-500">
                Sign as <strong className="capitalize">{myParty}</strong> when ready — both parties must sign to lock this {phase}.
              </span>
            )}
            {myPartySigned && !completed && (
              <Badge variant="secondary" className="text-xs">Awaiting {myParty === "customer" ? "vendor" : "customer"} signature</Badge>
            )}
          </div>

          {!myPartySigned && (
            <div className="rounded-xl border border-border p-3 bg-neutral-50/50 overflow-hidden">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Your signature ({myParty})</Label>

              {/* Iter 67ed — damage acknowledgement gate. The user must
                  tick this before the signature is accepted. The exact
                  text is echoed on the rental-agreement PDF so it
                  doubles as the legal anchor that no off-diagram damage
                  was observed at handover. */}
              <label
                className="mt-2 mb-3 flex items-start gap-2 cursor-pointer select-none"
                data-testid={`damage-ack-${phase}`}
              >
                <input
                  type="checkbox"
                  checked={damageAck}
                  onChange={(e) => setDamageAck(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                  data-testid={`damage-ack-check-${phase}`}
                />
                <span className="text-xs text-neutral-700 leading-snug">
                  I confirm the diagram above captures{" "}
                  <strong className="text-neutral-900">every</strong> visible damage on
                  the vehicle at this {phase}. No further pre-existing damage is being
                  withheld. Any damage discovered later that is not plotted here may be
                  charged against the deposit per the rental terms.
                </span>
              </label>

              <div className="mt-1 max-w-full">
                <SignaturePad
                  value={drawing}
                  onChange={setDrawing}
                  width={320}
                  height={120}
                  testId={`checklist-sign-${phase}`}
                />
              </div>
              <Button
                size="sm"
                onClick={sign}
                disabled={signing || !drawing || !damageAck}
                className="rounded-full mt-2"
                data-testid={`apply-sign-${phase}`}
              >
                {signing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                Apply signature & lock my side
              </Button>
              {!damageAck && drawing && (
                <p className="text-[11px] text-amber-700 mt-2" data-testid={`damage-ack-required-${phase}`}>
                  Tick the box above to confirm the damage diagram is complete before signing.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {completed && (
        <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Locked {new Date(phaseState.completed_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function SignatureSlotCompact({ party, image, signedAt }) {
  return (
    <div className="text-xs">
      <div className="text-neutral-500 uppercase tracking-wider mb-1 capitalize">{party}</div>
      {image ? (
        <>
          <img src={image} alt={`${party} signature`} className="max-h-12 max-w-full object-contain" />
          <div className="text-[10px] text-neutral-400 mt-0.5">{signedAt ? new Date(signedAt).toLocaleString() : ""}</div>
        </>
      ) : (
        <div className="h-12 rounded border border-dashed border-neutral-300 flex items-center justify-center text-neutral-300 text-[10px]">Not signed</div>
      )}
    </div>
  );
}

function OverageBanner({ overage, bookingId, userIsVendor, onWithheld }) {
  const [busy, setBusy] = useState(false);
  // One-click withhold: pre-fills the same /deposit/withhold endpoint the existing
  // deposit panel uses, with a server-side audit reason that mentions the mileage math
  // for traceability. Disabled unless the viewer is the vendor (or admin); customers
  // see the banner read-only so they know what's being charged + can dispute via chat.
  const withhold = async () => {
    if (!window.confirm(`Withhold €${overage.overage_eur.toFixed(2)} from the deposit for ${overage.overage_km} km over the included allowance?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/deposit/withhold`, {
        amount: overage.overage_eur,
        reason: `Mileage overage: ${overage.actual_km} km driven, ${overage.included_km} km included, ${overage.overage_km} km × €${overage.extra_km_rate}/km = €${overage.overage_eur.toFixed(2)}`,
      });
      toast.success(`Withheld €${overage.overage_eur.toFixed(2)} from the deposit.`);
      onWithheld?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 print:break-inside-avoid" data-testid="mileage-overage-banner">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-800">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-amber-900">Mileage overage detected</div>
          <p className="text-sm text-amber-800 mt-1">
            Renter drove <strong>{overage.actual_km} km</strong> over {overage.included_km} km included.
            That's <strong>{overage.overage_km} km</strong> beyond the daily cap × €{overage.extra_km_rate}/km =
            <strong> €{overage.overage_eur.toFixed(2)}</strong> to withhold from the deposit.
          </p>
          {userIsVendor ? (
            <Button
              size="sm"
              onClick={withhold}
              disabled={busy}
              className="rounded-full mt-3 bg-amber-600 hover:bg-amber-700"
              data-testid="overage-withhold-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Withhold €{overage.overage_eur.toFixed(2)} from deposit
            </Button>
          ) : (
            <p className="text-xs text-amber-700 mt-2">
              The vendor can withhold this amount from your deposit. Open the chat above if you'd like to dispute.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


// Iter 67ec — body styles for the inspection diagram. Order mirrors the
// car-creation TYPES list so it reads naturally to vendors. The agreement
// defaults to the car's `car_type`, but vendors / admins can override
// per-booking — useful when the car listing is generic ("SUV") but the
// physical body shape ("Crossover", "Estate", "Cabrio") needs to be
// recorded to anchor accident / damage notes.
const BODY_TYPE_OPTIONS = [
  "Economy",
  "Compact",
  "Hatchback",
  "Sedan",
  "Wagon / Estate",
  "Coupe",
  "Cabriolet / Convertible",
  "SUV",
  "Crossover",
  "Off-road / 4x4",
  "Minivan",
  "Van / Minivan",
  "MPV",
  "Pickup",
  "Luxury",
  "Sport",
  "Electric",
  "Hybrid",
  "Electric & Hybrid",
];

function BodyTypeBanner({ data, bookingId, canEdit, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(data?.body_type || "");
  }, [data?.body_type, editing]);

  const value = (data?.body_type || "").trim() || "—";

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${bookingId}/agreement`, { body_type: draft.trim() });
      toast.success("Body type updated");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not update body type");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-3 sm:p-4 flex items-center gap-3"
      data-testid="checklist-body-type-banner"
    >
      <span className="h-10 w-10 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center ring-1 ring-amber-500/30 shrink-0">
        <CarIcon className="h-5 w-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-700">
          Body type / Car body style
        </div>
        {!editing ? (
          <div className="mt-0.5 font-display text-base sm:text-lg font-semibold text-neutral-900 truncate" data-testid="checklist-body-type-value">
            {value}
          </div>
        ) : (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Select value={draft} onValueChange={setDraft}>
              <SelectTrigger className="h-9 w-56 rounded-lg" data-testid="checklist-body-type-select">
                <SelectValue placeholder="Pick a body type" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {BODY_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt} data-testid={`checklist-body-type-option-${opt}`}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Or type your own…"
              maxLength={120}
              className="h-9 w-48 rounded-lg"
              data-testid="checklist-body-type-input"
            />
          </div>
        )}
        <div className="mt-0.5 text-[11px] text-neutral-500 leading-snug">
          Used to identify the vehicle when recording accidents, scratches, dents or damages during pickup &amp; drop-off inspection.
        </div>
      </div>
      {canEdit && (
        editing ? (
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" onClick={save} disabled={busy} className="h-8 rounded-full bg-neutral-900 hover:bg-neutral-800 text-amber-400" data-testid="checklist-body-type-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy} className="h-8 rounded-full" data-testid="checklist-body-type-cancel">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            className="h-8 rounded-full text-amber-700 hover:bg-amber-100 shrink-0"
            data-testid="checklist-body-type-edit"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        )
      )}
    </div>
  );
}
